import { enqueueActivityEvent, isActivityQueueEnabled } from 'services/activityTracking/activityEventQueue';
import { getProducerSessionId, nextEventId, nextProducerSequence } from 'services/activityTracking/session';
import { ACTIVITY_EVENT_TYPE, ACTIVITY_YMS_EXPORT_RESULT } from 'utils/activityTracking/constants';
import { buildActivityEvent } from 'utils/activityTracking/envelope';
import { mapYmsExportStatusToResult } from 'utils/activityTracking/ymsExport';

/**
 * Export to YMS attempts as `yms_export.requested` / `yms_export.responded` pairs.
 *
 * Every click that passes the button's guards is an attempt. One attempt per transaction at a time -
 * the export flow itself guarantees that (the button is disabled while a request is pending, and the
 * saga runs one export per transaction). The lifecycle of an attempt:
 *
 *   begin    the click passed every guard: send `requested` right away and remember the attempt
 *   confirm  AIQ accepted the request: remember its request id
 *   complete Fenix answered: send `responded` with its result
 *   fail     the export failed to start - an error AIQ answered with, or a 400 / 5xx / network
 *            failure: send `responded(error)`
 *   cancel   a 409: another attempt was already pending or completed. Forget the attempt; its
 *            `requested` stays without a pair
 *   drop     the transaction closed before Fenix answered. Forget the attempt; its `requested` stays
 *            without a pair
 *
 * `requested` goes into the queue on the click, so closing the transaction, reloading the page or
 * logging out before AIQ answers still records it: the queue is flushed on all three.
 *
 * `correlation_id` is the attempt id: a separate id created on the click and carried by both events,
 * NOT equal to either event's `event_id` - the same as the focus periods.
 *
 * FAIL OPEN. Every entry point is wrapped: tracking must never block or delay the export.
 */

/** transactionId -> `{ correlationId, workObjectId, attemptNumber, requestId, context }` */
const attempts = new Map();

function toKey(transactionId) {
    return transactionId === null || transactionId === undefined || transactionId === '' ? null : String(transactionId);
}

function normalizeContext(context) {
    return {
        locationId: context?.locationId ?? null,
        gateId: context?.gateId ?? null,
        deskId: context?.deskId ?? null,
    };
}

function emit(intent) {
    if (!isActivityQueueEnabled()) return;
    enqueueActivityEvent(
        buildActivityEvent({
            eventId: nextEventId(),
            occurredAt: new Date().toISOString(),
            producerSessionId: getProducerSessionId(),
            producerSequence: nextProducerSequence(),
            ...intent,
        })
    );
}

function emitAttemptEvent(attempt, eventType, ymsResult = null) {
    emit({
        eventType,
        workObjectId: attempt.workObjectId,
        // The export's target IS the transaction, so `target_id` repeats it (the backend checks this).
        targetId: attempt.workObjectId,
        correlationId: attempt.correlationId,
        attemptNumber: attempt.attemptNumber,
        ymsResult,
        ...attempt.context,
    });
}

export function beginYmsExportAttempt({ transactionId, attemptNumber, context } = {}) {
    try {
        const key = toKey(transactionId);
        if (!key || !isActivityQueueEnabled()) return;

        const attempt = {
            correlationId: nextEventId(),
            workObjectId: key,
            attemptNumber,
            requestId: null,
            context: normalizeContext(context),
        };
        attempts.set(key, attempt);
        emitAttemptEvent(attempt, ACTIVITY_EVENT_TYPE.YMS_EXPORT_REQUESTED);
    } catch (error) {
        console.warn('activityTracking: yms export begin failed.', error);
    }
}

export function confirmYmsExportAttempt({ transactionId, requestId } = {}) {
    try {
        const attempt = attempts.get(toKey(transactionId));
        if (attempt && requestId !== null && requestId !== undefined) attempt.requestId = String(requestId);
    } catch (error) {
        console.warn('activityTracking: yms export confirm failed.', error);
    }
}

export function failYmsExportAttempt({ transactionId } = {}) {
    try {
        const key = toKey(transactionId);
        const attempt = attempts.get(key);
        if (!attempt) return;
        attempts.delete(key);
        emitAttemptEvent(attempt, ACTIVITY_EVENT_TYPE.YMS_EXPORT_RESPONDED, ACTIVITY_YMS_EXPORT_RESULT.ERROR);
    } catch (error) {
        console.warn('activityTracking: yms export fail failed.', error);
    }
}

/** A 409. The attempt's `requested` was already sent on the click and stays without a pair. */
export function cancelYmsExportAttempt({ transactionId } = {}) {
    try {
        attempts.delete(toKey(transactionId));
    } catch (error) {
        console.warn('activityTracking: yms export cancel failed.', error);
    }
}

export function completeYmsExportAttempt({ transactionId, requestId, status } = {}) {
    try {
        const ymsResult = mapYmsExportStatusToResult(status);
        if (!ymsResult) return;

        const key = toKey(transactionId);
        const attempt = attempts.get(key);
        if (!attempt) return;

        // A response for a different request than the one we are tracking (an older attempt, or another
        // window's) must not close ours. A missing id on either side cannot disprove the match.
        //
        // BEST EFFORT before `confirm`: until AIQ's HTTP reply gives us our request id, ANY response for
        // this transaction closes the attempt, because Fenix can legitimately answer before that reply
        // (see the "beats the HTTP reply" test). So a stale or another window's response that lands in
        // that short window closes ours with its result. The export flow allows one export per
        // transaction at a time, which keeps this rare; waiting for `confirm` would lose the common case.
        const incomingRequestId = requestId === null || requestId === undefined ? null : String(requestId);
        if (attempt.requestId && incomingRequestId && attempt.requestId !== incomingRequestId) return;

        attempts.delete(key);
        emitAttemptEvent(attempt, ACTIVITY_EVENT_TYPE.YMS_EXPORT_RESPONDED, ymsResult);
    } catch (error) {
        console.warn('activityTracking: yms export complete failed.', error);
    }
}

/** The transaction was closed before Fenix answered. The attempt stays a `requested` without a pair. */
export function dropYmsExportAttempt({ transactionId } = {}) {
    try {
        attempts.delete(toKey(transactionId));
    } catch (error) {
        console.warn('activityTracking: yms export drop failed.', error);
    }
}

export function hasYmsExportAttempt(transactionId) {
    return attempts.has(toKey(transactionId));
}

/** Logout, closing every panel, and the test seam. */
export function resetYmsExportTracker() {
    attempts.clear();
}
